import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, PackageCheck, MapPin, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function FeedbackPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/feedback/${token}`)
      .then((r) => { setInfo(r.data); if (r.data.submitted) setDone(true); })
      .catch((e) => setError(apiErr(e)));
  }, [token]);

  const submit = async () => {
    if (rating < 1) return toast.error("Please select a star rating");
    setSubmitting(true);
    try {
      await api.post(`/feedback/${token}`, { rating, comment });
      setDone(true);
      toast.success("Thank you for your feedback!");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSubmitting(false); }
  };

  if (error) return (
    <Shell>
      <div className="text-center py-16" data-testid="feedback-error">
        <div className="text-2xl font-heading font-black">Link not found</div>
        <p className="text-muted-foreground mt-2">{error}</p>
      </div>
    </Shell>
  );
  if (!info) return <Shell><div className="py-20 text-center text-muted-foreground">Loading…</div></Shell>;

  const shownRating = done ? (info.feedback?.rating || rating) : (hover || rating);

  return (
    <Shell>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary"><PackageCheck className="h-4 w-4" /> Delivery Complete</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">How was your move?</h1>
        <p className="text-muted-foreground text-sm mt-1">Order <span className="font-mono-num text-primary">{info.order_number}</span> · {info.customer_name}</p>
      </div>

      <div className="border border-border bg-card p-4 mb-6 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4 text-primary" /> {info.pickup} → {info.delivery}</div>
        {info.partner_name && <div className="mt-2">Logistics Partner: <span className="font-medium text-foreground">{info.partner_name}</span></div>}
      </div>

      {done ? (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-8 text-center" data-testid="feedback-thankyou">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <div className="font-heading font-bold text-2xl">Thank you!</div>
          <p className="text-muted-foreground text-sm mt-1">Your feedback has been recorded.</p>
          <div className="flex items-center justify-center gap-1 mt-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`h-6 w-6 ${n <= shownRating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
            ))}
          </div>
          {info.feedback?.comment && <p className="text-sm text-muted-foreground italic mt-3">"{info.feedback.comment}"</p>}
        </div>
      ) : (
        <div className="space-y-6" data-testid="feedback-form">
          <div>
            <div className="text-sm font-bold uppercase tracking-wide mb-3">Rate your experience</div>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${n}`}
                  aria-label={`${n} star`}
                >
                  <Star className={`h-9 w-9 ${n <= shownRating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                </button>
              ))}
              {rating > 0 && <span className="ml-2 font-mono-num font-bold text-lg">{rating}/5</span>}
            </div>
          </div>
          <div>
            <div className="text-sm font-bold uppercase tracking-wide mb-2">Tell us more (optional)</div>
            <Textarea
              className="rounded-none"
              rows={4}
              placeholder="How was the delivery and the partner's service?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="feedback-comment"
            />
          </div>
          <Button
            className="rounded-none w-full h-12 font-bold uppercase tracking-wide"
            onClick={submit}
            disabled={submitting}
            data-testid="submit-feedback-btn"
          >
            {submitting ? "Submitting…" : "Submit Feedback"}
          </Button>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border">
        <div className="max-w-xl mx-auto px-5 py-4"><Logo className="h-8" /></div>
      </div>
      <div className="max-w-xl mx-auto px-5 py-10">{children}</div>
    </div>
  );
}
